import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="binary-toggle"
export default class extends Controller {
  static targets = ["type"]
  static values = {
    dependentField: String
  }
  
  // Constants to avoid magic strings
  static VALUES = {
    TRUE: "1",
    FALSE: "0"
  }
  
  static BUTTON_CLASSES = {
    INACTIVE: "white black-text",
    ACTIVE: ""
  }

  connect() {
    // Initialize button states based on hidden field value
    this.updateButtonStyles()
    this.toggleDependentFields()
    
    // Listen for change events on hidden field (from button clicks OR programmatic changes)
    this.typeTarget.addEventListener("change", () => {
      this.updateButtonStyles()
      this.toggleDependentFields()
    })
  }
  
  handleClick(event) {
    const clickedButton = event.currentTarget
    const siblingButton = this.getSiblingButton(clickedButton)
    
    // Determine value based on which button was clicked
    const isTrue = clickedButton.textContent.trim() === this.getTrueLabel()
    
    // Update hidden field value
    this.typeTarget.value = isTrue ? this.constructor.VALUES.TRUE : this.constructor.VALUES.FALSE
    
    // Update button styles
    this.applyButtonStyles(clickedButton, siblingButton)
    
    // Dispatch change event for other controllers to listen
    this.typeTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
  
  // Helper method to update button styles
  updateButtonStyles() {
    const buttons = this.element.querySelectorAll('.btn')
    if (buttons.length < 2) return
    
    const currentValue = this.typeTarget.value === this.constructor.VALUES.TRUE
    const trueButton = Array.from(buttons).find(btn => btn.textContent.trim() === this.getTrueLabel())
    const falseButton = Array.from(buttons).find(btn => btn.textContent.trim() !== this.getTrueLabel())
    
    if (currentValue) {
      trueButton.classList.remove(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
      falseButton.classList.add(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
    } else {
      falseButton.classList.remove(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
      trueButton.classList.add(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
    }
  }
  
  // Apply styles to clicked and unclicked buttons
  applyButtonStyles(activeButton, inactiveButton) {
    // Remove inactive styles from active button
    activeButton.classList.remove(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
    
    // Add inactive styles to inactive button
    inactiveButton.classList.add(...this.constructor.BUTTON_CLASSES.INACTIVE.split(' '))
  }
  
  // Get the sibling button of the clicked button
  getSiblingButton(button) {
    const allButtons = Array.from(this.element.querySelectorAll('.btn'))
    const buttonIndex = allButtons.indexOf(button)
    return buttonIndex === 0 ? allButtons[1] : allButtons[0]
  }
  
  // Helper to get the true label from the button text
  getTrueLabel() {
    return this.element.querySelector('.btn')?.textContent.trim() || ''
  }

  toggleDependentFields() {
    // Use the camelCased version of the attribute name
    const dependentFieldId = this.element.dataset.binaryToggleDependentField
    if (!dependentFieldId) return

    const dependentElement = document.getElementById(dependentFieldId)
    if (!dependentElement) return

    const isVisible = this.typeTarget.value === this.constructor.VALUES.TRUE
    dependentElement.style.display = isVisible ? 'block' : 'none'
  }
}
